import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  IconPlus,
  IconPencil,
  IconArchive,
  IconArchiveOff,
  IconCash
} from '@tabler/icons-react';
import { api } from '../services/api';
import { balancesFromTangible } from '../utils/tangibleBalances';
import payeeCacheService from '../services/payeeCache';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useCanEdit, useIsOwner } from '../components/RoleGate.jsx';

import { MaskedCurrency } from '../components/MaskedValue';
import { resolveAmountColor } from '../components/AmountDisplay';
import { PageHero, SectionContainer, Row, ActionBar } from '../components/ds';
import { CardSkeleton, BalanceSkeleton } from '../components/SkeletonLoader';
import { CenteredLoader, InlineLoader } from '../components/LoadingIndicator';
import { useTimezoneAwareDateFormatting } from '../components/TimezoneAwareDateDisplay';
import { useToast } from '../components/ToastNotification';
import { SectionErrorBoundary } from '../components/ErrorBoundary';
import { LEDGER_TYPE_CATEGORY, LEDGER_TYPE_ICON } from '../utils/ledgerIcon';
import {
  LEDGER_GROUPS,
  calculateLastBillPaid,
  checkingLedgerNames,
  lastBillLinesPath,
  showsLastBillPaid,
} from '../utils/ledgerGroups';

const LEDGER_TYPE_MAP = {
  0: 'Checking',
  1: 'Savings',
  2: 'Credit Card'
};

const getLedgerTypeDisplay = (type) => LEDGER_TYPE_MAP[type] || 'Other';

const Ledgers = ({ isAuthReady = false }) => {
  const navigate = useNavigate();
  const { activeGroupId } = useGroup();
  const canEdit = useCanEdit();
  const isOwner = useIsOwner();
  const theme = useTheme();
  const { palette, ds } = theme;
  // v4 (#1377): xs gets the pinned ActionBar add button; sm+ uses per-section
  // [+] buttons. JS-conditional (not CSS) so the ledger-fab guide anchor
  // exists exactly once in the DOM per viewport.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { formatDate } = useTimezoneAwareDateFormatting();
  const { showSuccess, showError, showWarning } = useToast();
  const [ledgers, setLedgers] = useState([]);
  const [archivedLedgers, setArchivedLedgers] = useState([]);
  const [ledgerBalances, setLedgerBalances] = useState({});
  const [ledgerLastBillPaid, setLedgerLastBillPaid] = useState({});
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [paydayLoading, setPaydayLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextPayday, setNextPayday] = useState(null);

  const [createDialog, setCreateDialog] = useState({
    open: false, name: '', type: 0, error: '', submitting: false
  });
  const [renameDialog, setRenameDialog] = useState({
    open: false, ledger: null, newName: '', error: '', submitting: false
  });
  const [archiveDialog, setArchiveDialog] = useState({
    open: false, ledger: null, submitting: false
  });
  const [unarchivingId, setUnarchivingId] = useState(null);

  const loadLedgers = useCallback(async () => {
    try {
      setLoading(true);
      setBalancesLoading(true);
      setError(null);

      if (!activeGroupId) return;
      const response = await api.get(activeGroupId, '/ledgers?includeArchived=true');
      const all = response.ledgers || [];
      const active = all.filter(l => !l.archived);
      const archived = all.filter(l => l.archived);
      setLedgers(active);
      setArchivedLedgers(archived);
      setLoading(false);

      // #2158: active balances ride one /tangible read (active-only since
      // #2156). #1983: archived ledgers load their balance too, for display
      // only, through their per-entity call because /tangible omits them;
      // they skip the last-bill-paid lines fetch.
      const tangiblePromise = api.get(activeGroupId, '/tangible').catch((err) => {
        console.warn('Failed to load ledger balances:', err);
        showWarning('Failed to load ledger balances', { duration: 3000 });
        return null;
      });
      const dataPromises = all.map(async (ledger) => {
        try {
          const [balanceResponse, linesResponse] = await Promise.all([
            ledger.archived
              ? api.get(activeGroupId, `/ledgers/${encodeURIComponent(ledger.ledgerId)}/balance`)
              : null,
            ledger.archived
              ? { lines: [] }
              : api.get(activeGroupId, lastBillLinesPath(ledger.ledgerId)).catch(() => ({ lines: [] }))
          ]);

          const lastBillPaid = showsLastBillPaid(ledger)
            ? calculateLastBillPaid(linesResponse.lines || [], checkingLedgerNames(active))
            : null;

          return {
            id: ledger.ledgerId,
            balance: ledger.archived ? (balanceResponse?.balanceCents ?? 0) / 100 : null,
            lastBillPaid,
          };
        } catch (err) {
          console.warn('Failed to load data for ledger:', ledger.ledgerId, err);
          showWarning(`Failed to load data for ${ledger.name}`, { duration: 3000 });
          return { id: ledger.ledgerId, balance: ledger.archived ? 0 : null, lastBillPaid: null };
        }
      });

      const [tangible, dataResults] = await Promise.all([tangiblePromise, Promise.all(dataPromises)]);
      const { ledgers: activeCents } = balancesFromTangible(tangible);
      const balances = {};
      const lastBillPaidData = {};
      dataResults.forEach(result => {
        balances[result.id] = result.balance ?? (activeCents[result.id] ?? 0) / 100;
        lastBillPaidData[result.id] = result.lastBillPaid;
      });
      setLedgerBalances(balances);
      setLedgerLastBillPaid(lastBillPaidData);
      setBalancesLoading(false);
    } catch (err) {
      console.error('Failed to load ledgers:', err);
      const errorMessage = err.message || 'Failed to load ledgers. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
      setLoading(false);
      setBalancesLoading(false);
    }
  }, [activeGroupId, showError, showWarning]);

  const loadNextPayday = useCallback(async () => {
    try {
      setPaydayLoading(true);
      const response = await api.get(activeGroupId, '/next-payday');
      setNextPayday(response.nextPayday);
    } catch (err) {
      console.error('Failed to load next payday:', err);
      showWarning('Failed to load payday information', { duration: 3000 });
    } finally {
      setPaydayLoading(false);
    }
  }, [activeGroupId, showWarning]);

  useEffect(() => {
    if (isAuthReady && activeGroupId) {
      loadLedgers();
      loadNextPayday();
    }
  }, [isAuthReady, activeGroupId, loadLedgers, loadNextPayday]);

  const openCreate = (type = 0) => {
    setCreateDialog({ open: true, name: '', type, error: '', submitting: false });
  };

  const closeCreate = () => {
    setCreateDialog({ open: false, name: '', type: 0, error: '', submitting: false });
  };

  const handleCreate = async () => {
    const trimmed = (createDialog.name || '').trim();
    if (!trimmed) {
      setCreateDialog(prev => ({ ...prev, error: 'Name is required' }));
      return;
    }
    try {
      setCreateDialog(prev => ({ ...prev, submitting: true, error: '' }));
      const res = await api.post(activeGroupId, '/ledgers', { name: trimmed, type: createDialog.type });
      // The ledger came with its linked payee (#1944): the picker must see it.
      await payeeCacheService.invalidatePayeeCache('ledger_payee_linked');
      showSuccess(`Ledger "${trimmed}" created`);
      closeCreate();
      // Land on the new ledger's detail page so the user can start adding
      // lines straight away (and the guide's add-line steps have their
      // anchors present). Fall back to the list if the id is missing.
      const newLedgerId = res && res.ledger && res.ledger.ledgerId;
      if (newLedgerId) {
        navigate(`/ledgers/${encodeURIComponent(newLedgerId)}`);
      } else {
        loadLedgers();
      }
    } catch (err) {
      console.error('Failed to create ledger:', err);
      const errType = err.response?.data?.error?.type;
      if (errType === 'CONFLICT_ERROR' || /already exists/i.test(err.message || '')) {
        setCreateDialog(prev => ({ ...prev, submitting: false, error: 'A ledger with this name already exists.' }));
      } else if (errType === 'VALIDATION_ERROR') {
        setCreateDialog(prev => ({ ...prev, submitting: false, error: err.response.data.error.message }));
      } else {
        setCreateDialog(prev => ({ ...prev, submitting: false, error: err.message || 'Create failed.' }));
      }
    }
  };

  const openRename = (ledger) => {
    setRenameDialog({ open: true, ledger, newName: ledger.name, error: '', submitting: false });
  };

  const closeRename = () => {
    setRenameDialog({ open: false, ledger: null, newName: '', error: '', submitting: false });
  };

  const handleRename = async () => {
    const { ledger, newName } = renameDialog;
    const trimmed = (newName || '').trim();
    if (!trimmed) {
      setRenameDialog(prev => ({ ...prev, error: 'Name is required' }));
      return;
    }
    if (trimmed === ledger.name) {
      closeRename();
      return;
    }
    try {
      setRenameDialog(prev => ({ ...prev, submitting: true, error: '' }));
      await api.put(activeGroupId, `/ledgers/${encodeURIComponent(ledger.ledgerId)}`, {
        name: trimmed,
        version: ledger.version
      });
      // The linked payee was renamed with the ledger (#1944).
      await payeeCacheService.invalidatePayeeCache('ledger_payee_linked');
      showSuccess(`Renamed to "${trimmed}"`);
      closeRename();
      loadLedgers();
    } catch (err) {
      console.error('Failed to rename ledger:', err);
      const errType = err.response?.data?.error?.type;
      if (/payee named/i.test(err.message || '')) {
        // The ledger's payee would collide with another payee (#1944).
        setRenameDialog(prev => ({ ...prev, submitting: false, error: err.message }));
      } else if (errType === 'CONFLICT_ERROR' || /already exists/i.test(err.message || '')) {
        setRenameDialog(prev => ({ ...prev, submitting: false, error: 'A ledger with this name already exists.' }));
      } else if (errType === 'VALIDATION_ERROR') {
        setRenameDialog(prev => ({ ...prev, submitting: false, error: err.response.data.error.message }));
      } else {
        setRenameDialog(prev => ({ ...prev, submitting: false, error: err.message || 'Rename failed.' }));
      }
    }
  };

  const openArchive = (ledger) => {
    setArchiveDialog({ open: true, ledger, submitting: false });
  };

  const closeArchive = () => {
    setArchiveDialog({ open: false, ledger: null, submitting: false });
  };

  const handleArchive = async () => {
    const { ledger } = archiveDialog;
    try {
      setArchiveDialog(prev => ({ ...prev, submitting: true }));
      await api.delete(activeGroupId, `/ledgers/${encodeURIComponent(ledger.ledgerId)}`);
      showSuccess(`Archived "${ledger.name}"`);
      closeArchive();
      loadLedgers();
    } catch (err) {
      console.error('Failed to archive ledger:', err);
      showError(err.message || 'Failed to archive ledger.');
      setArchiveDialog(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleUnarchive = async (ledger) => {
    try {
      setUnarchivingId(ledger.ledgerId);
      await api.put(activeGroupId, `/ledgers/${encodeURIComponent(ledger.ledgerId)}`, {
        archived: false,
        version: ledger.version
      });
      showSuccess(`Restored "${ledger.name}"`);
      loadLedgers();
    } catch (err) {
      console.error('Failed to unarchive ledger:', err);
      showError(err.message || 'Failed to unarchive ledger.');
    } finally {
      setUnarchivingId(null);
    }
  };

  const handleLedgerClick = (ledgerId) => {
    navigate(`/ledgers/${ledgerId}`);
  };

  const activateOnKey = (e, ledgerId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLedgerClick(ledgerId);
    }
  };

  // §11 value coloring via the pure resolver shared with AmountDisplay. We render
  // the number with MaskedCurrency (a plain inheriting span) so it picks up the
  // Row/SectionContainer font scale (13px / 11px) instead of AmountDisplay's own
  // 16px Typography. Masking is preserved by MaskedCurrency itself.
  const renderAmount = (dollars, variant) => {
    const value = typeof dollars === 'number' ? dollars : 0;
    const cents = Math.round(value * 100);
    return (
      <MaskedCurrency
        value={value}
        style={{ color: resolveAmountColor(palette, variant, cents < 0, cents === 0) }}
      />
    );
  };

  const renderLedgerRow = (ledger) => {
    const balance = ledgerBalances[ledger.ledgerId] ?? 0;
    const lastBillPaid = ledgerLastBillPaid[ledger.ledgerId];
    const Icon = LEDGER_TYPE_ICON[ledger.type] || IconCash;
    const category = LEDGER_TYPE_CATEGORY[ledger.type] || 'neutral';
    const meta = showsLastBillPaid(ledger)
      ? `Last bill paid: ${balancesLoading ? '...' : (lastBillPaid ? formatDate(lastBillPaid.dateStamp, 'date-only') : '-')}`
      : undefined;

    // v4 (#1377, convention 1): rename/archive reveal on row hover via the Row
    // primitive's action band - 15px muted glyphs per the board.
    const ownerActions = isOwner ? (
      <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Rename">
          <IconButton
            size="small"
            aria-label={`Rename ${ledger.name}`}
            onClick={() => openRename(ledger)}
            sx={{ color: palette.text.secondary, padding: '2px' }}
          >
            <IconPencil size={15} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Archive">
          <IconButton
            size="small"
            aria-label={`Archive ${ledger.name}`}
            onClick={() => openArchive(ledger)}
            sx={{ color: palette.text.secondary, padding: '2px' }}
          >
            <IconArchive size={15} />
          </IconButton>
        </Tooltip>
      </Box>
    ) : undefined;

    return (
      <Row
        key={ledger.ledgerId}
        data-tour-id="ledger-row"
        category={category}
        icon={<Icon />}
        name={ledger.name}
        meta={meta}
        value={balancesLoading ? <BalanceSkeleton width="60px" /> : renderAmount(balance, 'row')}
        actions={ownerActions}
        onClick={() => handleLedgerClick(ledger.ledgerId)}
        role="button"
        tabIndex={0}
        aria-label={ledger.name}
        onKeyDown={(e) => activateOnKey(e, ledger.ledgerId)}
      />
    );
  };

  const renderArchivedRow = (ledger) => {
    const Icon = LEDGER_TYPE_ICON[ledger.type] || IconCash;
    const category = LEDGER_TYPE_CATEGORY[ledger.type] || 'neutral';

    const restoreAction = isOwner ? (
      <Box onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Restore">
          <span>
            <IconButton
              size="small"
              aria-label={`Restore ${ledger.name}`}
              onClick={() => handleUnarchive(ledger)}
              disabled={unarchivingId === ledger.ledgerId}
              sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
            >
              <IconArchiveOff size={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ) : undefined;

    return (
      <Row
        key={ledger.ledgerId}
        category={category}
        icon={<Icon />}
        name={ledger.name}
        meta={`${getLedgerTypeDisplay(ledger.type)} · Archived`}
        value={balancesLoading ? <BalanceSkeleton width="60px" /> : renderAmount(ledgerBalances[ledger.ledgerId] ?? 0, 'row')}
        actions={restoreAction}
        onClick={() => handleLedgerClick(ledger.ledgerId)}
        role="button"
        tabIndex={0}
        aria-label={ledger.name}
        onKeyDown={(e) => activateOnKey(e, ledger.ledgerId)}
        sx={{ opacity: 0.7, '&:hover': { opacity: 0.9 } }}
      />
    );
  };

  if (!isAuthReady) {
    return <CenteredLoader message="Initializing..." />;
  }

  if (loading) {
    return (
      <Box sx={{ pb: 8 }}>
        <PageHero label="Ledgers" />
        <Box sx={{ mt: { xs: '12px', md: '14px' } }}>
          <CardSkeleton count={3} />
        </Box>
      </Box>
    );
  }

  // v4 board payday aux: 11px dim label over a 14px value.
  const paydayAux = (
    <SectionErrorBoundary title="Payday Information Error">
      <Box sx={{ textAlign: 'right' }}>
        <Typography component="div" sx={{ fontSize: '11px', color: palette.text.dim }}>
          Next payday
        </Typography>
        {paydayLoading ? (
          <Box sx={{ mt: '4px', display: 'flex', justifyContent: 'flex-end' }}>
            <BalanceSkeleton width="80px" />
          </Box>
        ) : (
          <Typography
            component="div"
            sx={{
              fontSize: '14px',
              fontVariantNumeric: 'tabular-nums',
              color: nextPayday ? palette.interactive : palette.text.dim,
              marginTop: '2px',
            }}
          >
            {nextPayday || 'Unavailable'}
          </Typography>
        )}
      </Box>
    </SectionErrorBoundary>
  );

  // The published intro guide highlights [data-tour-id="ledger-fab"]; the FAB
  // is gone in v4, so the anchor lands on exactly one add affordance per
  // viewport: the mobile ActionBar button, else the first rendered group's
  // [+], else the empty-state button.
  const firstGroupWithLedgers = LEDGER_GROUPS.find(
    (g) => ledgers.some((l) => l.type === g.type)
  )?.type;

  const isEmpty = ledgers.length === 0 && archivedLedgers.length === 0;

  // #1983: the Archived subtotal sums every archived ledger, signed as each
  // one is, since all three types count toward Net Worth. Display only.
  const archivedTotal = archivedLedgers.reduce(
    (sum, l) => sum + (ledgerBalances[l.ledgerId] ?? 0),
    0
  );

  return (
    <Box sx={{ pb: 8 }}>
      <PageHero
        label="Ledgers"
        back={{ label: 'Dashboard', onClick: () => navigate('/') }}
        aux={paydayAux}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 3, mb: 1 }}>
          {error}
        </Alert>
      )}

      {isEmpty ? (
        <Box
          sx={{
            mt: 3,
            backgroundColor: palette.surface.raised,
            border: `1px solid ${palette.divider}`,
            borderRadius: `${ds.radius.container}px`,
            padding: `${ds.space[8]}px ${ds.space[6]}px`,
          }}
        >
          <Typography sx={{ fontSize: `${ds.font.h1}px`, color: palette.text.primary, mb: 1 }}>
            No visible ledgers found
          </Typography>
          <Typography sx={{ fontSize: `${ds.font.row}px`, color: palette.text.dim }}>
            Ledgers represent your accounts (checking, savings, credit cards, etc.).
          </Typography>
          {canEdit && !isMobile && (
            <Button
              variant="contained"
              color="primary"
              data-tour-id="ledger-fab"
              startIcon={<IconPlus size={16} />}
              onClick={() => openCreate()}
              sx={{ mt: 2 }}
            >
              Add ledger
            </Button>
          )}
        </Box>
      ) : (
        <SectionErrorBoundary title="Ledger List Error">
          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            {LEDGER_GROUPS.map((group) => {
              const groupLedgers = ledgers.filter((l) => l.type === group.type);
              if (groupLedgers.length === 0) return null;

              const groupTotal = groupLedgers.reduce(
                (sum, l) => sum + (ledgerBalances[l.ledgerId] ?? 0),
                0
              );

              return (
                <SectionContainer
                  key={group.type}
                  data-ledger-group={group.type}
                  title={group.label}
                  count={groupLedgers.length}
                  category={group.category}
                  density={isMobile ? 'compact' : 'default'}
                  subtotal={
                    balancesLoading
                      ? <BalanceSkeleton width="70px" />
                      : renderAmount(groupTotal, 'subtotal')
                  }
                  onAdd={canEdit && !isMobile ? () => openCreate(group.type) : undefined}
                  addButtonProps={
                    group.type === firstGroupWithLedgers && !isMobile
                      ? { 'data-tour-id': 'ledger-fab' }
                      : undefined
                  }
                >
                  {groupLedgers.map(renderLedgerRow)}
                </SectionContainer>
              );
            })}
          </Box>

          {archivedLedgers.length > 0 && (
            <Box sx={{ mt: 5 }}>
              <SectionContainer
                title="Archived"
                count={archivedLedgers.length}
                category="neutral"
                collapsible
                defaultCollapsed
                recessed
                density={isMobile ? 'compact' : 'default'}
                subtotal={
                  balancesLoading
                    ? <BalanceSkeleton width="70px" />
                    : renderAmount(archivedTotal, 'subtotal')
                }
                note="restore returns to active"
              >
                <Typography
                  sx={{
                    fontSize: `${ds.font.dim}px`,
                    color: palette.text.dim,
                    paddingTop: `${ds.space[2]}px`,
                    paddingBottom: `${ds.space[2]}px`,
                  }}
                >
                  Archived ledgers retain their line history. Click a card to view details, or restore to bring it back to the active list.
                </Typography>
                {archivedLedgers.map(renderArchivedRow)}
              </SectionContainer>
            </Box>
          )}
        </SectionErrorBoundary>
      )}

      {/* v4 (#1377): the FAB is retired - xs gets the pinned ActionBar with a
          full-width add button (quick-add sheets land with the detail screens);
          sm+ uses the per-section [+] buttons above. */}
      {canEdit && isMobile && (
        <ActionBar hint="tap a ledger to open">
          <Button
            fullWidth
            variant="contained"
            color="primary"
            aria-label="add ledger"
            data-tour-id="ledger-fab"
            startIcon={<IconPlus size={16} />}
            onClick={() => openCreate()}
          >
            Add ledger
          </Button>
        </ActionBar>
      )}

      {/* Create Ledger Dialog */}
      <Dialog open={createDialog.open} onClose={createDialog.submitting ? undefined : closeCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Ledger</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Ledger Name"
            data-tour-id="ledger-create-name"
            fullWidth
            variant="outlined"
            value={createDialog.name}
            onChange={(e) => setCreateDialog(prev => ({ ...prev, name: e.target.value, error: '' }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !createDialog.submitting) {
                e.preventDefault();
                handleCreate();
              }
            }}
            error={!!createDialog.error}
            helperText={createDialog.error || 'Enter a unique name for your ledger'}
            disabled={createDialog.submitting}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined" disabled={createDialog.submitting} data-tour-id="ledger-type-select">
            <InputLabel>Ledger Type</InputLabel>
            <Select
              value={createDialog.type}
              onChange={(e) => setCreateDialog(prev => ({ ...prev, type: e.target.value }))}
              label="Ledger Type"
            >
              <MenuItem value={0}>Checking</MenuItem>
              <MenuItem value={1}>Savings</MenuItem>
              <MenuItem value={2}>Credit Card</MenuItem>
            </Select>
            <FormHelperText>Type is set at creation and cannot be changed later.</FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate} disabled={createDialog.submitting}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            data-tour-id="ledger-create-btn"
            disabled={createDialog.submitting || !(createDialog.name || '').trim()}
          >
            {createDialog.submitting ? <InlineLoader message="Creating..." size={16} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Ledger Dialog */}
      <Dialog open={renameDialog.open} onClose={renameDialog.submitting ? undefined : closeRename} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Ledger</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Ledger Name"
            fullWidth
            variant="outlined"
            value={renameDialog.newName}
            onChange={(e) => setRenameDialog(prev => ({ ...prev, newName: e.target.value, error: '' }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !renameDialog.submitting) {
                e.preventDefault();
                handleRename();
              }
            }}
            error={!!renameDialog.error}
            helperText={renameDialog.error || ' '}
            disabled={renameDialog.submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRename} disabled={renameDialog.submitting}>Cancel</Button>
          <Button
            onClick={handleRename}
            variant="contained"
            disabled={renameDialog.submitting || !(renameDialog.newName || '').trim()}
          >
            {renameDialog.submitting ? <InlineLoader message="Saving..." size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialog.open} onClose={archiveDialog.submitting ? undefined : closeArchive} maxWidth="sm" fullWidth>
        <DialogTitle>Archive Ledger</DialogTitle>
        <DialogContent>
          <Typography>
            Archive <strong>{archiveDialog.ledger?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            The ledger will be hidden from the active list. Lines stay intact and you can restore it at any time from the Archived section below.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeArchive} disabled={archiveDialog.submitting}>Cancel</Button>
          <Button
            onClick={handleArchive}
            variant="contained"
            color="warning"
            disabled={archiveDialog.submitting}
          >
            {archiveDialog.submitting ? <InlineLoader message="Archiving..." size={16} /> : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Ledgers;
